import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Cookies } from "react-cookie";
import { AppStateType } from "..";
import { externalLoginUrl, ssoUrl } from "../../constants/api";
import { getRequest, ssoLoginFromCookieRequest, ssoLoginRequest } from "../../services/apiService";
import { SignInFromCookieType, SignInType } from "../../types/auth";
import { AuthDataType, AuthStateType, SignInReturnType, UserType } from "../../types/authSlice";

/** Clears WordPress + GSA SSO cookies read by `getSsoLoginCookie` so cookie-login cannot restore the session. */
const clearClientSsoCookies = () => {
  if (typeof document === "undefined") return;

  const cookies = new Cookies();
  const cookieDomain = process.env.REACT_APP_SSO_COOKIE_DOMAIN;
  const expire = "Thu, 01 Jan 1970 00:00:01 GMT";
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const wpRemoveOpts: { path: string; domain?: string } = { path: "/" };
  if (cookieDomain) {
    wpRemoveOpts.domain = cookieDomain;
  }

  for (const name of Object.keys(cookies.getAll())) {
    if (name.startsWith("wordpress_logged_in_")) {
      cookies.remove(name, wpRemoveOpts);
    }
  }

  if (cookieDomain) {
    document.cookie = `GSA_LOGIN_KEY=; Path=/; Expires=${expire}; Domain=${cookieDomain}${secure}`;
    document.cookie = `GSA_LOGIN=; Path=/; Expires=${expire}; Domain=${cookieDomain}${secure}`;
  }
  document.cookie = `GSA_LOGIN_KEY=; Path=/; Expires=${expire}${secure}`;
  document.cookie = `GSA_LOGIN=; Path=/; Expires=${expire}${secure}`;
};

export const signIn = createAsyncThunk<SignInReturnType, SignInType>("auth/signIn", async (body, { dispatch }) => {
  const authenticateResponse = await ssoLoginRequest(`${ssoUrl}login`, body);

  if (authenticateResponse.status !== 200) {
    return { error: authenticateResponse.data?.message || authenticateResponse.data?.error || "Authentication failed" };
  }

  const responseData = authenticateResponse.data?.data || authenticateResponse.data;
  if (!responseData) {
    return { error: "Invalid response from server" };
  }

  const user = {
    cookie: responseData.cookie,
    display_name: responseData.user?.DisplayName || responseData.user?.display_name,
    email: responseData.user?.Email || responseData.user?.email,
    username: responseData.user?.Username || responseData.user?.username,
    permissions: responseData.user?.Permissions || responseData.user?.permissions || [],
  };

  const data = {
    user,
    token: responseData.token,
    cookie: responseData.cookie,
    cookieName: responseData.cookieName,
  };

  dispatch(setApiToken(responseData.token));
  localStorage.setItem("gambod", JSON.stringify(data));

  const profile = await getRequest("profile");
  const exportAllowed = profile?.allowed?.export ?? false;
  dispatch(setExport(exportAllowed));
  localStorage.setItem("export", JSON.stringify(exportAllowed));

  return {
    data,
  };
});

export const signInFromCookie = createAsyncThunk<SignInReturnType, SignInFromCookieType>("auth/signIn", async (body, { dispatch }) => {
  const authenticateResponse = await ssoLoginFromCookieRequest(`${ssoUrl}loginFromCookie`, body);

  if (authenticateResponse.status !== 200) {
    return { error: authenticateResponse.data?.message || authenticateResponse.data?.error || "Authentication failed" };
  }

  const responseData = authenticateResponse.data?.data || authenticateResponse.data;
  if (!responseData) {
    return { error: "Invalid response from server" };
  }

  const user = {
    cookie: responseData.cookie,
    display_name: responseData.user?.DisplayName || responseData.user?.display_name,
    email: responseData.user?.Email || responseData.user?.email,
    username: responseData.user?.Username || responseData.user?.username,
    permissions: responseData.user?.Permissions || responseData.user?.permissions || [],
  };

  const data = {
    user,
    token: responseData.token,
    cookie: responseData.cookie,
    cookieName: responseData.cookieName,
  };

  dispatch(setApiToken(responseData.token));
  localStorage.setItem("gambod", JSON.stringify(data));

  const profile = await getRequest("profile");
  const exportAllowed = profile?.allowed?.export ?? false;
  dispatch(setExport(exportAllowed));
  localStorage.setItem("export", JSON.stringify(exportAllowed));

  return {
    data,
  };
});

export const getApiToken = createAsyncThunk<void, string>("auth/getApiToken", async (email, { dispatch }) => {
  dispatch(setApiToken(undefined));
  // removed for messing up with member's accounts authorisation

  /*   const apiResponse: ApiResponseType = await getRequest(`get-token?email=${email}`);

  if (apiResponse.token) {
    try {
      const gambod = localStorage.getItem("gambod");

      if (gambod) {
        const data = JSON.parse(gambod);

        localStorage.setItem(
          "gambod",
          JSON.stringify({
            user: data.user,
            token: apiResponse.token,
          })
        );
        dispatch(setApiToken(apiResponse.token));
        dispatch(setApiToken(undefined));
      }
    } catch (error) {
      dispatch(setApiToken(undefined));
    }
  } else {
    dispatch(setApiToken(undefined));
  } */
});

const initialState: AuthStateType = {
  token: undefined,
  user: undefined,
  export: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    onSignIn: (state, { payload: { user, token } }: PayloadAction<{ user: UserType; token: string }>) => {
      state.user = user;
      state.token = token;
    },
    signOut: state => {
      clearClientSsoCookies();
      localStorage.removeItem("gambod");
      localStorage.removeItem("export");
      state.user = undefined;
      state.token = undefined;
      state.export = false;
      if (typeof window !== "undefined") {
        window.location.replace(externalLoginUrl);
      }
    },
    setApiToken: (state, { payload }: PayloadAction<string | undefined>) => {
      state.token = payload;
    },
    setExport: (state, { payload }: PayloadAction<boolean>) => {
      state.export = payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(signIn.fulfilled, (state, { payload }: PayloadAction<SignInReturnType>) => {
      if (payload.data) {
        state = payload.data;
      }
    });
  },
});

export const { onSignIn, signOut, setApiToken, setExport } = authSlice.actions;

export const selectAuthSlice = (state: AppStateType) => state.auth;

export const selectUserDisplayName = createSelector(selectAuthSlice, state => state.user?.display_name);

export const selectSaveToken = createSelector(selectAuthSlice, state => state.token);

export const selectUsername = createSelector(selectAuthSlice, state => state.user?.email);

export const selectUser = createSelector(selectAuthSlice, state => state.user);

export const selectPermissions = createSelector(selectAuthSlice, state => state?.user?.permissions || []);

export const selectExport = createSelector(selectAuthSlice, state => state?.export);

export default authSlice.reducer;
